import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import { generateQueryParams } from "../../utils/generateQueryParams";

// NOTE: Create Order from Accepted Offer
export const createOrder = createAsyncThunk(
  "order/createOrder",
  async (orderData) => {
    const response = await api.post("/user/", orderData);
    return response.data;
  },
);

// NOTE: Get All User Orders
export const getUserOrders = createAsyncThunk(
  "order/getUserOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = generateQueryParams(params);
      const url = query ? `/user/myOrders?${query}` : "/user/myOrders";

      const response = await api.get(url);

      if (!response.data) {
        return rejectWithValue({
          message: "Invalid response format from server",
        });
      }

      return response.data;
    } catch (error) {
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Server error: ${error.response.status}`;

        return rejectWithValue({
          message: errorMessage,
          status: error.response.status,
          data: error.response.data,
        });
      } else if (error.request) {
        return rejectWithValue({
          message: "Network error. Please check your connection.",
          type: "network",
        });
      } else {
        return rejectWithValue({
          message: error.message || "An unexpected error occurred",
          type: "unknown",
        });
      }
    }
  },
);

export const getOrderDetails = createAsyncThunk(
  "order/getOrderDetails",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/user/myOrders/${orderId}/details`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch order details" },
      );
    }
  },
);

// NOTE: Get All Orders with Tab-based Filtering
export const getAllOrdersWithFilters = createAsyncThunk(
  "order/getAllOrdersWithFilters",
  async ({ tab = "all", page = 1, limit = 10 } = {}) => {
    const response = await api.get(
      `orders?tab=${tab}&page=${page}&limit=${limit}`,
    );
    return response.data;
  },
);

// NOTE: Update Order (status, milestones, split options)
export const updateOrder = createAsyncThunk(
  "order/updateOrder",
  async ({ orderId, updateData }) => {
    const response = await api.put(`orders/${orderId}`, updateData);
    return response.data;
  },
);

// NOTE: Delete Order
export const deleteOrder = createAsyncThunk(
  "order/deleteOrder",
  async (orderId) => {
    const response = await api.delete(`orders/${orderId}`);
    return response.data;
  },
);

// NOTE: get unadded/available client team members for an order
export const getAvailableTeamMemberClients = createAsyncThunk(
  "order/getAvailableTeamMemberClients",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/user/myOrders/${orderId}/unadded-client-team-members`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to fetch available team members",
        },
      );
    }
  },
);

// NOTE: Add client team members to order
export const addClientTeamMembers = createAsyncThunk(
  "order/addClientTeamMembers",
  async (
    { orderId, teamMemberIds, canViewSensitiveData = false },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/user/myOrders/${orderId}/client-team-members`,
        {
          teamMemberIds,
          canViewSensitiveData,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to add team members" },
      );
    }
  },
);

// Upload files to order
export const uploadOrderFiles = createAsyncThunk(
  "order/uploadOrderFiles",
  async ({ orderId, files, note, category }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user/myOrders/${orderId}/files`, {
        files, // Array of file objects with URLs
        note,
        category,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to upload files" },
      );
    }
  },
);

// Get order files
export const getOrderFiles = createAsyncThunk(
  "order/getOrderFiles",
  async ({ orderId, category, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (category) params.category = category;

      const query = new URLSearchParams(params).toString();
      const response = await api.get(
        `/user/myOrders/${orderId}/files?${query}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch files" },
      );
    }
  },
);

// Delete order file
export const deleteOrderFile = createAsyncThunk(
  "order/deleteOrderFile",
  async ({ orderId, fileId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/user/myOrders/${orderId}/files/${fileId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete file" },
      );
    }
  },
);

// Client adds order note
export const addOrderNote = createAsyncThunk(
  "order/addOrderNote",
  async ({ orderId, content, attachments = [] }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user/myOrders/${orderId}/notes`, {
        content,
        attachments,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to add order note" },
      );
    }
  },
);

// Add new milestone to order
export const createMilestone = createAsyncThunk(
  "order/createMilestone",
  async (
    {
      orderId,
      title,
      description,
      amount,
      dueDate,
      notes,
      files = [],
      splitOptionsEnabled = false,
      splitOptions = [],
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(`/user/myOrders/${orderId}/milestone`, {
        title,
        description,
        amount,
        dueDate,
        notes,
        files,
        splitOptionsEnabled,
        splitOptions,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create milestone" },
      );
    }
  },
);

export const approveMilestone = createAsyncThunk(
  "order/approveMilestone",
  async (
    {
      orderId,
      milestoneId,
      title,
      description,
      amount,
      dueDate,
      notes,
      files = [],
      splitOptionsEnabled = false,
      splitOptions = [],
      offerMilestoneId,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put(
        `/user/myOrders/${orderId}/milestone/${milestoneId}/approve`,
        {
          title,
          description,
          amount,
          dueDate,
          notes,
          files,
          splitOptionsEnabled,
          splitOptions,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to approve milestone" },
      );
    }
  },
);

export const addMilestoneNote = createAsyncThunk(
  "milestone/addNote",
  async (
    {
      orderId,
      milestoneProgressId,
      content,
      parentCommentId,
      attachments = [],
    },
    { rejectWithValue },
  ) => {
    console.log("addMilestoneNote thunk called with:", {
      orderId,
      milestoneProgressId,
      content,
      parentCommentId,
      attachments,
    });
    try {
      const response = await api.post(
        `/user/myOrders/${orderId}/milestones/${milestoneProgressId}/comments`,
        {
          content,
          parentCommentId,
          attachments,
        },
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add comment",
      );
    }
  },
);

// Accept Milestone Thunk
export const acceptMilestone = createAsyncThunk(
  "order/acceptMilestone",
  async ({ orderId, milestoneId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/user/myOrders/${orderId}/milestones/${milestoneId}/accept`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to accept milestone" },
      );
    }
  },
);

// Request Revision Thunk
export const requestRevision = createAsyncThunk(
  "order/requestRevision",
  async (
    { orderId, milestoneId, reason, files = [], comments },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/user/myOrders/${orderId}/milestones/${milestoneId}/request-revision`,
        {
          reason,
          files,
          comments,
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to request revision" },
      );
    }
  },
);

// Request Order-level Revision Thunk
export const requestOrderRevision = createAsyncThunk(
  "order/requestOrderRevision",
  async ({ orderId, reason, files = [], comments }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user/${orderId}/revisions`, {
        reason,
        files,
        comments,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to request order revision" },
      );
    }
  },
);

// Accept Revision Thunk
export const acceptRevision = createAsyncThunk(
  "order/acceptRevision",
  async ({ orderId, milestoneId }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/user/myOrders/${orderId}/milestones/${milestoneId}/accept-revision`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to accept revision" },
      );
    }
  },
);

// Add Order Feedback
export const addOrderFeedback = createAsyncThunk(
  "order/addOrderFeedback",
  async ({ orderId, rating, note }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/user/myOrders/${orderId}/feedback`, {
        rating,
        note,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Add Milestone Feedback
export const addMilestoneFeedback = createAsyncThunk(
  "order/addMilestoneFeedback",
  async ({ orderId, milestoneId, rating, note }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/user/myOrders/${orderId}/milestones/${milestoneId}/feedback`,
        {
          rating,
          note,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addReporterDispute = createAsyncThunk(
  "order/addReporterDispute",
  async ({ orderId, disputeType, description }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/user/myOrders/${orderId}/disputes`, {
        disputeType,
        description,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Accept Order-level Revision Thunk
export const acceptOrderRevision = createAsyncThunk(
  "order/acceptOrderRevision",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/user/myOrders/${orderId}/accept-revision`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to accept order revision" },
      );
    }
  },
);

export const acceptOrder = createAsyncThunk(
  "order/acceptOrder",
  async ({ orderId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/myOrders/${orderId}/accept`);
      dispatch(getOrderDetails(orderId));

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: error.message || "Failed to accept order",
        },
      );
    }
  },
);

export const getReferralOrderSummary = createAsyncThunk(
  "order/getReferralOrderSummary",
  async (orderId) => {
    const response = await api.get(
      `/user/myOrders/${orderId}/referral-order-summary`,
    );
    return response.data;
  },
);
